/**
 * Radius options for the pharmacy map radius selector (MKP-2054).
 * Confirmed against Figma 547 Handoff, section "Radius picker for pickup
 * farmacies": fixed 10/15/20/25 km rows plus an unbounded "Any radius" row.
 * There is no 50 km option — that came from the superseded Map PRD.
 */
export const RADIUS_OPTIONS_KM = [10, 15, 20, 25] as const;

export const ANY_RADIUS = "any" as const;

export const DEFAULT_RADIUS_KM = 10;

export type RadiusKm = (typeof RADIUS_OPTIONS_KM)[number];

export type RadiusOptionValue = RadiusKm | typeof ANY_RADIUS;

/**
 * Structurally compatible with PharmacyMarker and with the frozen
 * getVendorsFiltered item shape: `distance` is in METERS (PostGIS
 * ST_Distance on geography) and is absent when no location was resolved.
 */
export interface PharmacyWithDistance {
	distance?: number | null;
}

export interface RadiusOption {
	value: RadiusOptionValue;
	count: number;
}

const METERS_PER_KM = 1000;

/**
 * Boundary-inclusive radius check (`distance === radiusKm * 1000` is inside).
 * Pharmacies with an unknown distance are kept, mirroring
 * capPharmaciesByRadiusKm so the set is not emptied before a location
 * is resolved.
 */
export const isPharmacyWithinRadiusKm = (
	pharmacy: PharmacyWithDistance,
	radiusKm: number,
): boolean =>
	pharmacy.distance == null || pharmacy.distance <= radiusKm * METERS_PER_KM;

export const filterPharmaciesByRadius = <T extends PharmacyWithDistance>(
	pharmacies: readonly T[],
	radius: RadiusOptionValue,
): T[] =>
	radius === ANY_RADIUS
		? [...pharmacies]
		: pharmacies.filter((pharmacy) =>
				isPharmacyWithinRadiusKm(pharmacy, radius),
			);

/**
 * Builds the selector rows: one count per km option (pharmacies inside that
 * radius) and the unfiltered total for "Any radius".
 */
export const buildRadiusOptions = (
	pharmacies: readonly PharmacyWithDistance[],
): RadiusOption[] => [
	...RADIUS_OPTIONS_KM.map((radiusKm) => ({
		value: radiusKm as RadiusOptionValue,
		count: pharmacies.filter((pharmacy) =>
			isPharmacyWithinRadiusKm(pharmacy, radiusKm),
		).length,
	})),
	{ value: ANY_RADIUS, count: pharmacies.length },
];

/**
 * Picks the initially selected radius: the 10 km default when it yields
 * results, otherwise the narrowest option that does. Figma shows the
 * selection moving to 15 km when 10 km is empty and therefore disabled.
 */
export const resolveDefaultRadius = (
	options: readonly RadiusOption[],
): RadiusOptionValue =>
	options.find(
		(option) => option.value === DEFAULT_RADIUS_KM && option.count > 0,
	)?.value ??
	options.find((option) => option.count > 0)?.value ??
	ANY_RADIUS;

/**
 * Uses a session-persisted radius when that option still has results for
 * the current page. Otherwise the ordinary data-driven default. Derived
 * during render so restoring cannot setState after the map is already open
 * (that extra commit faded the picker content in a loop).
 */
export const resolveSessionOrDefaultRadius = (
	options: readonly RadiusOption[],
	persisted: RadiusOptionValue | null,
): RadiusOptionValue => {
	if (persisted == null) {
		return resolveDefaultRadius(options);
	}
	const persistedOption = options.find((option) => option.value === persisted);
	if (persistedOption && persistedOption.count > 0) {
		return persisted;
	}
	return resolveDefaultRadius(options);
};

/** Parses a Radix Select string value back into a typed radius option. */
export const parseRadiusOptionValue = (raw: string): RadiusOptionValue => {
	const radiusKm = Number(raw);
	return RADIUS_OPTIONS_KM.find((option) => option === radiusKm) ?? ANY_RADIUS;
};

/**
 * Reads a persisted selection back. Values the control no longer offers are
 * rejected so a stale session entry cannot pin the map to a dropped option.
 * This only checks that the value is a *legal* option — the caller still has
 * to check it has results for the current data before applying it (MKP-2054:
 * a legal-but-empty restore is what unmounted MapLibre on 2026-08-21).
 */
export const parsePersistedRadius = (
	raw: string | null,
): RadiusOptionValue | null => {
	if (raw === ANY_RADIUS) {
		return ANY_RADIUS;
	}
	return RADIUS_OPTIONS_KM.find((option) => String(option) === raw) ?? null;
};
